import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { 
    Button, 
    Box, 
    Typography, 
    CircularProgress,
    TextField,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import { TokenValidator, SimpleValidators, STYLES } from '../../utils';
import { Orders } from '../../controllers';
import { CsvDialog, OrderLoader } from './internal';

export default function Main(){
    const nav = useNavigate();

    const [token, setToken] = React.useState(undefined);

    const [records, setRecords] = React.useState([]);
    const [display, setDisplay] = React.useState([]);
    const [filters, setFilters] = React.useState({
        date: '',
        reference: '',
        tag: '',
        status: '',
        parts: '',
        lastUpdate: '',
    });

    const [filterCallback, setFilterCallback] = React.useState(undefined);
    const [filtering, isFiltering] = React.useState(false);

    const [csvUi, showCsvUi] = React.useState(false);
    const [loaderUi, showLoaderUi] = React.useState(false);

    React.useEffect(() => {
        handleLoad();
    },[]);

    React.useEffect(() => {
        if(token === undefined){
            return;
        }
        getRecords();
    },[token]);

    React.useEffect(() => {
        if(records.length < 1){
            setDisplay([]);
            return;
        }

        applyFilters();
    },[records]);

    React.useEffect(() => {
        if(filterCallback !== undefined){
            clearTimeout(filterCallback);
        }

        var hasFilters = false;
        Object.keys(filters).forEach(k => {
            if(filters[k]){
                hasFilters = true;
                return;
            }
        });

        if(!hasFilters){
            if(records.length < 1){
                setDisplay([]);
            }else{
                setDisplay(records);
            }

            isFiltering(false);
            return;
        }

        const timeout = setTimeout(applyFilters, 250);
        setFilterCallback(timeout);
    },[filters]);

    const handleLoad = async() =>{
        await AdquireToken();
    }

    const handleFilter = async(e)  => {
        setFilters({...filters, [e.target.name]: e.target.value});
        isFiltering(true);
    }

    const AdquireToken = async() => {
        if(token !== undefined){
            return;
        }
        
        const result = await TokenValidator.GetClientUser();
        
        setToken(result);
    }
    
    const getRecords = async() => {
        const response =  await Orders.GetDetailByCompany(token);
        
        if(!response.success){
            console.log('Error retriving records. Please, reload page');
            return;
        }
        
        var data = [...response.result];

        data.forEach(async(d, i) => {
            d.date_create = new Date(d.date_create).toLocaleDateString('en-US')
            const noParts = await SimpleValidators.IsVoid(d.parts);
            if(noParts ){
                d['generalStatus'] =  'Processing';    
                d['lastUpdate'] =  'Unknown';    
            }else{
                var status = await SimpleValidators.Uniques(d.parts, 'status');
                var dates = await SimpleValidators.Uniques(d.parts, 'date_update');
                var date = await SimpleValidators.HigherDate(dates);
                d['generalStatus'] =  status.length > 1 ? 'Mixed' : status[0];    
                d['lastUpdate'] =  date;    
            }
        });
        
        setRecords(data);
    }
    
    const applyFilters = async() => {
        var result = [];

        const regex = await {
            date_create     : new RegExp(`^${(SimpleValidators.ScapingRegex(filters.date)).toLowerCase().replace(/\*/g, ".*")}`),
            reference_id    : new RegExp(`^${(SimpleValidators.ScapingRegex(filters.reference)).toLowerCase().replace(/\*/g, ".*")}`),
            case_serial     : new RegExp(`^${(SimpleValidators.ScapingRegex(filters.tag)).toLowerCase().replace(/\*/g, ".*")}`), 
            status          : new RegExp(`^${(SimpleValidators.ScapingRegex(filters.status)).toLowerCase().replace(/\*/g, ".*")}`),
            generalStatus   : new RegExp(`^${(SimpleValidators.ScapingRegex(filters.parts)).toLowerCase().replace(/\*/g, ".*")}`),
            lastUpdate     : new RegExp(`^${(SimpleValidators.ScapingRegex(filters.lastUpdate)).toLowerCase().replace(/\*/g, ".*")}`),
        }

        result = records.filter((item) => {
            var cycle = true;

            Object.keys(regex).forEach(r => {
                const value = item[r].toString().toLowerCase();
                if(!regex[r].test(value)){
                    cycle = false;
                }
            });

            return cycle;
        });

        setDisplay(result);
        isFiltering(false);
    }

    const getCsvReport = async(dates, statuses, callback) => {
        const response = await Orders.GetCsvReport(token, dates, statuses);

        if(!response.ok){
            const result = await response.json();
            await callback(result);
            return
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob); // Create a downloadable URL
        const a = document.createElement('a');
        a.href = url;
        a.download = `report-${Date.now()}.csv`; // Suggested file name
        document.body.appendChild(a);
        a.click();
        a.remove(); 
        await callback({success: true});
    }

    return(
        <Box
            sx={{
                padding:2,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CsvDialog 
                open={csvUi}
                onClose={() => showCsvUi(false)}
                onConfirm={getCsvReport}
            />

            <OrderLoader
                open={loaderUi}
                onClose={() => showLoaderUi(false)}
                onConfirm={getRecords}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                }}
            >
                <Typography variant='h5' display={'block-inline'}>
                    Registered Orders
                </Typography>
                <Button
                    variant='contained'
                    size='small'
                    startIcon={
                        <AddCircleIcon />
                    }
                    onClick={() => showLoaderUi(true)}
                >
                    Load Orders
                </Button>
            </Box>
            
            <Box
                sx={{
                    marginTop:2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <Divider sx={{minWidth:'100%'}}></Divider>
                <Box
                    sx={{
                        marginTop:1,
                        display: 'flex',
                        flexDirection: 'row-reverse',
                        width:'100%'
                    }}
                >
                    <Button
                        variant='contained'
                        size='small'
                        color='success'
                        startIcon={
                            <CloudDownloadIcon />
                        }
                        onClick={() => showCsvUi(true)}
                    >
                        CSV
                    </Button>
                </Box>

                <TableContainer sx={{mt:2}}>
                    <Table
                        size='small'
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Order Date
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='date'
                                        onChange={handleFilter}
                                        value={filters.date}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Reference ID
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='reference'
                                        onChange={handleFilter}
                                        value={filters.reference}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Tag ID
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='tag'
                                        onChange={handleFilter}
                                        value={filters.tag}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Status
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='status'
                                        onChange={handleFilter}
                                        value={filters.status}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Parts
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='N/A' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        disabled
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Parts Status
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='parts'
                                        onChange={handleFilter}
                                        value={filters.parts}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Last Update
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='lastUpdate'
                                        onChange={handleFilter}
                                        value={filters.lastUpdate}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {!filtering && display.length > 0 && display.map((row) => (
                            <TableRow
                                key={row.id}
                            >
                                <TableCell align='center'>
                                    {row.date_create}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.reference_id}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.case_serial}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.status}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.parts !== null ? row.parts.length : 0}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.generalStatus}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.lastUpdate}
                                </TableCell>
                            </TableRow>
                        ))}
                        </TableBody>
                    </Table>
                    {!filtering && display.length < 1 && (
                        <Typography variant='h5'>
                            No records found
                        </Typography>
                    )}
                </TableContainer>
                {filtering && (
                    <Box
                        sx={{
                            marginTop: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Typography variant='h5'>
                            Loading Records
                        </Typography>
                        <CircularProgress />
                    </Box>
                )}
            </Box>
        </Box>
    );
}